import { useState, useEffect } from "react";
import { ArrowLeft, MapPin, Settings, ShieldAlert, Navigation, Map, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Capacitor } from "@capacitor/core";
import useApi from "@/utils/useApi";
import apis from "@/utils/apis";
import Modal from "./Modal";
import useGeolocation from "@/hooks/useGeolocation";
import toast from "react-hot-toast";
import MapLocationPicker from "./MapLocationPicker";

const PinCodeLocationSelector = ({ initialValues, onComplete, onCancel, isEdit = false, onStepChange }) => {
  const { t } = useTranslation();
  const { callApi: callNearestNearbyApi } = useApi();
  const userMobile = useSelector((state) => state.user.userInfo?.mobile);
  const [step, setStep] = useState("location"); // 'location' | 'form'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const {
    getCurrentPosition,
    requestEnableLocation,
    checkLocationEnabled,
    ensureLocationPermission,
    goToSettingsAndWait,
  } = useGeolocation();
  const [selectedLocation, setSelectedLocation] = useState(
    typeof initialValues.nearby === 'object' && initialValues.nearby !== null
      ? initialValues.nearby
      : null
  );
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [formData, setFormData] = useState({
    name: initialValues.name || "",
    mobile: initialValues.mobile || userMobile || "",
    address: initialValues.address || "",
    landmark: initialValues.landmark || "",
    city: initialValues.city || "",
    state: initialValues.state || "",
    country: initialValues.country || "",
    pin_code: initialValues.pin_code || "",
    nearby: initialValues.nearby?._id ?? initialValues.nearby ?? "",
    is_default: initialValues.is_default || false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // Notify parent about step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step, onStepChange]);

  // Warm up geolocation permission check on mount
  useEffect(() => {
    checkLocationEnabled();
  }, [checkLocationEnabled]);

  // ─── Nearest NearbyLocation lookup ────────────────────────────────────────
  const findAndSetNearbyFromCoords = async (latitude, longitude) => {
    const result = await callNearestNearbyApi({
      api: {
        url: `${apis.getNearestNearbyLocation.url}?latitude=${latitude}&longitude=${longitude}`,
        method: apis.getNearestNearbyLocation.method,
      },
    });
    if (result?.found && result?.nearbyLocation) {
      const loc = result.nearbyLocation;
      setSelectedLocation(loc);
      setFormData((prev) => ({
        ...prev,
        city: loc.city,
        state: loc.state,
        country: loc.country,
        pin_code: loc.pinCode?.toString() ?? '',
        nearby: loc._id,
      }));
      setStep('form');
      toast.success(`${loc.nearbyLocation}${loc.pinCode ? ` · ${loc.pinCode}` : ''}`, { icon: '📍' });
    } else {
      toast.error(t('addresses.noAreaNearby', 'No delivery area found near this location.'));
    }
  };

  // ─── GPS flow ─────────────────────────────────────────────────────────────
  const handleUseGPS = async () => {
    setIsDetectingLocation(true);
    try {
      const locationEnabled = await checkLocationEnabled();
      if (!locationEnabled) {
        const turnedOn = await requestEnableLocation();
        if (!turnedOn) {
          toast.error(t("addresses.locationTurnedOff", "Please enable location services."));
          return;
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (Capacitor.isNativePlatform()) {
        const permResult = await ensureLocationPermission();
        if (permResult === 'permanently_denied') { setShowPermissionModal(true); return; }
        if (permResult !== 'granted') {
          toast.error(t("addresses.errors.locationPermissionDenied", "Location permission denied."));
          return;
        }
      } else if (navigator.permissions) {
        try {
          const permResult = await navigator.permissions.query({ name: 'geolocation' });
          if (permResult.state === 'denied') { setShowPermissionModal(true); return; }
        } catch { /* unavailable */ }
      }
      const position = await getCurrentPosition();
      await findAndSetNearbyFromCoords(position.latitude, position.longitude);
    } catch (err) {
      if (err.type === 'PERMISSION_DENIED' || err.message === 'Location permission denied') {
        setShowPermissionModal(true);
      } else {
        toast.error(t("addresses.errors.locationDetectionFailed", "Failed to detect location. Please try again."));
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // ─── Map flow ──────────────────────────────────────────────────────────────
  const handleMapConfirm = async ({ latitude, longitude }) => {
    setShowMapPicker(false);
    setIsDetectingLocation(true);
    try {
      await findAndSetNearbyFromCoords(latitude, longitude);
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // Address form
  const validateField = (name, val) => {
    const value = val?.toString()
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = t("addresses.errors.nameRequired");
        break;
      case "mobile":
        if (!value.trim()) error = t("addresses.errors.mobileRequired");
        else if (value.length !== 10)
          error = t("addresses.errors.mobileInvalid");
        break;
      case "address":
        if (!value.trim()) error = t("addresses.errors.addressRequired");
        break;
      default:
        break;
    }
    setFormErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For mobile field, only allow numeric input
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '');
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      if (touchedFields[name]) validateField(name, numericValue);
      return;
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (touchedFields[name]) validateField(name, value);
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };
  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.mobile?.toString().trim().length === 10 &&
      formData.address.trim() &&
      !!formData.nearby &&
      !formErrors.name &&
      !formErrors.mobile &&
      !formErrors.address
    );
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    ["name", "mobile", "address"].forEach((field) => {
      setTouchedFields((prev) => ({ ...prev, [field]: true }));
      validateField(field, formData[field]);
    });
    if (!isFormValid()) return;
    
    setIsSubmitting(true);
    try {
      await onComplete({
        ...formData,
        location: selectedLocation?.location?.coordinates
          ? { coordinates: selectedLocation.location.coordinates }
          : null,
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === "form") setStep("location");
    else if (onCancel) onCancel();
  };

  return (
    <div className={step === "form" ? "space-y-4 min-h-[300px]" : "space-y-4"}>
      {/* Step 1: Location selection */}
      {step === "location" && (
        <div className="space-y-3 py-1">

          {/* GPS button */}
          <button
            type="button"
            onClick={handleUseGPS}
            disabled={isDetectingLocation}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
              {isDetectingLocation
                ? <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                : <Navigation className="w-5 h-5 text-green-600 dark:text-green-400" />
              }
            </div>
            <div className="text-left">
              <p className="font-semibold text-green-700 dark:text-green-400 text-sm">
                {t("addresses.useMyLocation", "Use my current location")}
              </p>
              <p className="text-xs text-green-600/70 dark:text-green-500/70 mt-0.5">
                {t("addresses.useMyLocationDesc", "We'll find the nearest delivery area")}
              </p>
            </div>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-grow border-t border-gray-200 dark:border-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500">{t("common.or", "or")}</span>
            <div className="flex-grow border-t border-gray-200 dark:border-gray-700" />
          </div>

          {/* Map button */}
          <button
            type="button"
            onClick={() => setShowMapPicker(true)}
            disabled={isDetectingLocation}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
              <Map className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm">
                {t("addresses.selectOnMap", "Pick on map")}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t("addresses.selectOnMapDesc", "Tap to pin your exact delivery spot")}
              </p>
            </div>
          </button>

          {/* Edit mode: keep current location */}
          {isEdit && initialValues.nearby && initialValues.latitude && initialValues.longitude && (
            <button
              type="button"
              onClick={() => setStep("form")}
              className="w-full text-center text-sm text-primary-100 hover:underline py-1"
            >
              {t("addresses.keepCurrentLocation", "Keep current location")} →
            </button>
          )}

        
        </div>
      )}

      {/* Address form */}
      {step === "form" && (
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="text-sm font-medium text-gray-700 mb-1"
              >
                {t("addresses.fullName")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                className={`w-full px-4 py-2 text-gray-700 bg-gray-100 border ${
                  touchedFields.name && formErrors.name
                    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:!border-primary-100"
                } rounded-lg focus:outline-none transition-colors duration-200`}
              />
              {touchedFields.name && formErrors.name && (
                <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="mobile"
                className="text-sm font-medium text-gray-700 mb-1"
              >
                {t("addresses.mobileNumber")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                maxLength={10}
                className={`w-full px-4 py-2 text-gray-700 bg-gray-100 border ${
                  touchedFields.mobile && formErrors.mobile
                    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:!border-primary-100"
                } rounded-lg focus:outline-none transition-colors duration-200`}
              />
              {touchedFields.mobile && formErrors.mobile && (
                <p className="mt-1 text-sm text-red-500">{formErrors.mobile}</p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="address"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              {t("addresses.address")} <span className="text-red-500">*</span>
            </label>
            {selectedLocation && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                {t("addresses.addressHint", { location: selectedLocation.nearbyLocation })}
              </p>
            )}
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              rows="3"
              placeholder={selectedLocation ? t("addresses.addressPlaceholder", { location: selectedLocation.nearbyLocation }) : t("addresses.enterYourAddress")}
              className={`w-full px-4 py-2 bg-gray-100 border ${
                touchedFields.address && formErrors.address
                  ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-300 focus:!border-primary-100"
              } rounded-lg focus:outline-none transition-colors duration-200 resize-none`}
            ></textarea>
            {touchedFields.address && formErrors.address && (
              <p className="mt-1 text-sm text-red-500">{formErrors.address}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="landmark"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              {t("addresses.landmark")}
            </label>
            {selectedLocation && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                {t("addresses.landmarkHint", { location: selectedLocation.nearbyLocation })}
              </p>
            )}
            <input
              type="text"
              id="landmark"
              name="landmark"
              value={formData.landmark}
              onChange={handleInputChange}
              placeholder={selectedLocation ? t("addresses.landmarkPlaceholder", { location: selectedLocation.nearbyLocation }) : t("addresses.landmarkGeneral")}
              className="w-full px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 focus:!border-primary-100 rounded-lg focus:outline-none transition-colors duration-200"
            />
          </div>
          
          {/* Default Address Checkbox */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="is_default"
                name="is_default"
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, is_default: e.target.checked }));
                }}
                className="w-4 h-4 text-primary-100 bg-gray-100 border-gray-300 rounded focus:ring-primary-100 focus:ring-2"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="is_default" className="text-sm font-medium text-gray-700 cursor-pointer">
                {t("addresses.setAsDefaultAddress", "Set as default address")}
              </label>
            </div>
          </div>
          
          <Modal.Footer>
            <div className="flex justify-end gap-2 border-none">
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white dark:bg-transparent hover:bg-gray-100 text-gray-700 text-sm font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className={`px-6 py-2 rounded-md text-white transition-colors duration-200 min-w-[140px] flex items-center justify-center ${
                  isFormValid() && !isSubmitting
                    ? "bg-primary-100 hover:bg-primary-100"
                    : "bg-primary-100 opacity-50 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <div className="loader !text-white" />
                ) : (
                  t("addresses.saveAndContinue")
                )}
              </button>
            </div>
          </Modal.Footer>
        </form>
      )}

      {/* Permission Denied Modal */}
      {showPermissionModal && (() => {
        const isNative = Capacitor.isNativePlatform();
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isPWAInstalled =
          window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true;
        let browserInstructionKey;
        if (isPWAInstalled && isIOS) {
          browserInstructionKey = 'browserInstructionsPwaIos';
        } else if (isPWAInstalled) {
          browserInstructionKey = 'browserInstructionsPwaAndroid';
        } else if (isIOS || isSafari) {
          browserInstructionKey = 'browserInstructionsSafari';
        } else {
          browserInstructionKey = 'browserInstructionsChrome';
        }
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
            onClick={() => setShowPermissionModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center">
                <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-full">
                  <ShieldAlert className="w-10 h-10 text-red-500" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-center text-gray-800 dark:text-gray-100">
                {t("locationGateModal.permissionDeniedTitle")}
              </h3>

              <p className="text-sm text-center text-gray-600 dark:text-gray-300 leading-relaxed">
                {isNative
                  ? t("locationGateModal.permissionDeniedNativeDesc")
                  : t(`locationGateModal.${browserInstructionKey}`)}
              </p>

              <div className="space-y-2">
                {isNative ? (
                  <button
                    onClick={async () => {
                      setShowPermissionModal(false);
                      const result = await goToSettingsAndWait();
                      if (result === 'granted') {
                        setTimeout(() => handleUseGPS(), 500);
                      }
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-md hover:from-blue-600 hover:to-blue-700 transition-all"
                  >
                    <Settings className="w-5 h-5" />
                    {t("locationGateModal.openSettings")}
                  </button>
                ) : (
                  <button
                      onClick={() => {
                      setShowPermissionModal(false);
                      setTimeout(() => handleUseGPS(), 300);
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-md hover:from-blue-600 hover:to-blue-700 transition-all"
                  >
                    <Settings className="w-5 h-5" />
                    {t("locationGateModal.tryAgain")}
                  </button>
                )}
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Map Picker */}
      {showMapPicker && (
        <MapLocationPicker
          onConfirm={handleMapConfirm}
          onCancel={() => setShowMapPicker(false)}
        />
      )}
    </div>
  );
};

export default PinCodeLocationSelector;
